import React, { useEffect, useState } from "react";
import axios from "axios";
import CryptoJS from "crypto-js";
import { message } from "antd";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import style from "css/signup/signupDetail.module.css";
import SEOMeta from "component/app/SEOMeta";

const PW_COMBO_REGEX =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^*()_+\-=[\]{}|:;"'<>,.?/]).+$/;

// 기존 all_place_user_client 로그인/비밀번호 설정 API와 동일한 AES256 CBC/PKCS7 패턴 적용
const encryptAes = (plain) => {
  const key = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY);
  return CryptoJS.AES.encrypt(plain, key, {
    iv: key,
    padding: CryptoJS.pad.Pkcs7,
    mode: CryptoJS.mode.CBC,
  }).toString();
};

function SignUpDetail() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const urlCode = searchParams.get("code");
  const stateEmail = location.state?.email;
  const stateCode = location.state?.code;
  const stateInviteYn = location.state?.inviteYn;
  const stateCompanyNm = location.state?.companyNm;
  const stateSignupType = location.state?.signupType;

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [company, setCompany] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [companyFocused, setCompanyFocused] = useState(false);
  const [companyActiveIdx, setCompanyActiveIdx] = useState(-1);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  useEffect(() => {
    if (stateEmail && stateCode) {
      setEmail(stateEmail);
      if (stateInviteYn) {
        setCompany(stateCompanyNm || "");
      }
      setLoading(false);
      return;
    }
    if (!urlCode) {
      navigate("/signup", { replace: true });
      return;
    }
    let cancelled = false;
    axios
      .post(
        `${process.env.REACT_APP_SERVER_URL}/user/getEmailByCode`,
        { CODE: urlCode },
      )
      .then((res) => {
        if (cancelled) return;
        const emailValue = res.data?.EMAIL;
        if (!emailValue) {
          // 200 응답인데 EMAIL 누락됐을 경우 스펙상 발생하지 않지만 혹시 몰라 방어코드 추가
          message.error(t("signup.detail.verifyInfoFail"));
          navigate("/signup", { replace: true });
          return;
        }
        navigate("/signup/detail", {
          state: {
            email: emailValue,
            code: urlCode,
            inviteYn:
              String(res.data?.INVITE_YN || res.data?.invite_yn || "")
                .toUpperCase() === "Y",
            companyNm:
              res.data?.COMPANY_NM ||
              res.data?.company_nm ||
              res.data?.companyNm ||
              "",
            signupType: res.data?.SIGNUP_TYPE || "",
          },
          replace: true,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const data = err?.response?.data;
        const errCode =
          typeof data === "string" ? data : data?.userMsg || data?.UserMsg;

        // 소셜 CODE 는 Auth0 토큰이 필요해 랜딩에서 재발급할 수 없다.
        if (
          errCode === "EXPIRED_SOCIAL_AUTHCODE" ||
          errCode === "USED_SOCIAL_AUTHCODE"
        ) {
          message.error(t("signup.detail.socialCodeExpired"));
          setTimeout(() => {
            if (cancelled) return;
            window.location.href = window.location.origin + "/allplace/";
          }, 2000);
          return;
        }

        if (errCode === "EXPIRED_AUTHCODE" || errCode === "USED_AUTHCODE") {
          navigate("/signup/expired", {
            state: { code: urlCode },
            replace: true,
          });
          return;
        }

        let text;
        if (errCode === "INVALID_AUTHCODE") {
          text = t("signup.detail.invalidLink");
        } else {
          text = t("signup.detail.tempError");
        }
        message.error(text);
        navigate("/signup", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [urlCode, stateEmail, stateCode, stateInviteYn, stateCompanyNm, navigate, t]);

  const isInvite = Boolean(stateInviteYn);
  const isSocial = stateSignupType === "SOCIAL";
  // 소셜 가입은 Auth0 가 인증을 담당하므로 비밀번호를 받지 않는다.
  // 다만 초대 가입은 서버가 비밀번호를 필수로 요구하므로 제외한다.
  const hidePassword = isSocial && !isInvite;

  // 초대 가입이 아닐 때만 등록된 회사 목록 로드
  useEffect(() => {
    if (isInvite) return;
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/user/company`)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data)
          ? res.data.map((c) => c?.COMPANY_NM).filter(Boolean)
          : [];
        setCompanyList(list);
      })
      .catch(() => {
        // 목록 로드에 실패해도 직접 입력으로 신규 등록은 가능하도록 예외처리 따로 안함
      });
    return () => {
      cancelled = true;
    };
  }, [isInvite]);

  if (loading) return null;

  const greetingName = email.split("@")[0] || "";

  const companyKeyword = company.trim();
  const companyKeywordLower = companyKeyword.toLowerCase();
  const companyMatches = companyKeyword
    ? companyList
        .filter((nm) => nm.toLowerCase().includes(companyKeywordLower))
        .slice(0, 50)
    : [];
  const companyExactExists = companyList.some(
    (nm) => nm.trim().toLowerCase() === companyKeywordLower,
  );
  const showNewCompanyRow = companyKeyword.length > 0 && !companyExactExists;
  const showCompanyDropdown =
    companyFocused &&
    companyKeyword.length > 0 &&
    (companyMatches.length > 0 || showNewCompanyRow);

  const companyOptions = [
    ...companyMatches.map((nm) => ({ type: "match", value: nm })),
    ...(showNewCompanyRow ? [{ type: "new" }] : []),
  ];

  const chooseCompanyOption = (opt) => {
    if (opt.type === "new") {
      setCompanyFocused(false); // 등록된 회사가 없으면 입력값 그대로 신규 등록
    } else {
      setCompany(opt.value);
      setCompanyFocused(false);
    }
    setCompanyActiveIdx(-1);
  };

  const handleCompanyKeyDown = (e) => {
    if (!showCompanyDropdown || companyOptions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCompanyActiveIdx((i) => (i + 1) % companyOptions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCompanyActiveIdx(
        (i) => (i - 1 + companyOptions.length) % companyOptions.length,
      );
    } else if (e.key === "Enter") {
      if (companyActiveIdx >= 0) {
        e.preventDefault();
        chooseCompanyOption(companyOptions[companyActiveIdx]);
      }
    } else if (e.key === "Escape") {
      setCompanyFocused(false);
      setCompanyActiveIdx(-1);
    }
  };

  const ruleMinLen = password.length >= 8;
  const ruleCombo = PW_COMBO_REGEX.test(password);
  const ruleNoEdgeSpace = password.length > 0 && password === password.trim();
  const allRulesPass = ruleMinLen && ruleCombo && ruleNoEdgeSpace;
  const passwordsMatch =
    password.length > 0 && password === passwordConfirm;
  const confirmMismatch =
    passwordConfirm.length > 0 && password !== passwordConfirm;

  const canSubmit =
    (isInvite || company.trim().length > 0) &&
    userName.trim().length > 0 &&
    (hidePassword || (allRulesPass && passwordsMatch)) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        CODE: stateCode,
        MEMBER_NM: userName.trim(),
      };
      if (!hidePassword) {
        payload.MEMBER_PW = encryptAes(password);
      }
      if (!isInvite) {
        payload.COMPANY_NM = company.trim();
      } else if (company.trim()) {
        payload.COMPANY_NM = company.trim();
      }
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/user/signup`,
        payload,
      );
      navigate("/signup/complete", { state: { completed: true } });
    } catch (err) {
      const data = err?.response?.data;
      const errCode =
        typeof data === "string" ? data : data?.userMsg || data?.UserMsg;

      let text = t("signup.detail.signupFail");
      if (errCode === "INVALID_AUTHCODE") {
        text = t("signup.detail.invalidLink");
      } else if (errCode === "EXPIRED_AUTHCODE") {
        text = t("signup.detail.linkExpired");
      } else if (errCode === "DUPLICATED_MEMBER_ID") {
        text = t("signup.detail.duplicatedMember");
      } else if (errCode === "DUPLICATED_EMAIL") {
        text = t("signup.detail.duplicatedEmail");
      } else if (errCode === "ERROR_PENDING_EMAIL_EXISTS") {
        text = t("signup.detail.pendingExists");
      } else if (errCode === "ERROR_SIGNUP") {
        text = t("signup.detail.errorSignup");
      }
      message.error(text);
      setSubmitting(false);

      // /user/signup 쿼리에 만료·사용여부 검사가 없어 현재는 도달하지 않는다.
      if (errCode === "EXPIRED_AUTHCODE") {
        if (isSocial) {
          window.location.href = window.location.origin + "/allplace/";
        } else {
          navigate("/signup", { replace: true });
        }
        return;
      }
      if (errCode === "INVALID_AUTHCODE") {
        navigate("/signup", { replace: true });
      }
    }
  };

  const eyeIconHide = `${process.env.REACT_APP_CLIENT_URL}img/signup/password_hide.svg`;
  const eyeIconOpen = `${process.env.REACT_APP_CLIENT_URL}img/signup/password_open.svg`;

  return (
    <div className={style.page}>
      <SEOMeta
        title={t("signup.detail.seo.title")}
        description={t("signup.detail.seo.description")}
        keywords={t("signup.detail.seo.keywords")}
      />
      <Link
        to="/"
        className={style.logoLink}
      >
        <img
          alt="all Place"
          src={`${process.env.REACT_APP_CLIENT_URL}img/common/allplace_logo_dark.png`}
        />
      </Link>
      <h1 className={style.title}>
        {t("signup.detail.welcome", { name: greetingName })}
      </h1>
      <p className={style.subtitle}>
        {isInvite
          ? t("signup.detail.subtitleInvite", { defaultValue: "초대를 수락하고 아래 정보를 입력해 주세요." })
          : t("signup.detail.subtitle")}
      </p>

      <div className={style.form}>
        <div className={`${style.field} ${style.companyField}`}>
          <img
            className={style.fieldIcon}
            src={`${process.env.REACT_APP_CLIENT_URL}img/signup/company.svg`}
            alt=""
          />
          <input
            className={`${style.input} ${isInvite ? style.inputLocked : ""}`}
            type="text"
            placeholder={t("signup.detail.companyPlaceholder")}
            value={company}
            readOnly={isInvite}
            disabled={isInvite}
            onChange={(e) => {
              if (isInvite) return;
              setCompany(e.target.value);
              setCompanyActiveIdx(-1);
            }}
            onFocus={() => !isInvite && setCompanyFocused(true)}
            onBlur={() => setCompanyFocused(false)}
            onKeyDown={handleCompanyKeyDown}
            autoComplete="off"
          />
          {!isInvite && showCompanyDropdown && (
            // onMouseDown preventDefault: 항목 클릭이 input blur보다 먼저 먹도록
            <div
              className={style.dropdown}
              onMouseDown={(e) => e.preventDefault()}
            >
              {companyOptions.map((opt, idx) => {
                const active = idx === companyActiveIdx;
                const scrollRef = (el) => {
                  if (active && el) el.scrollIntoView({ block: "nearest" });
                };
                if (opt.type === "new") {
                  return (
                    <button
                      type="button"
                      key="__new__"
                      ref={scrollRef}
                      className={`${style.dropdownNew} ${
                        active ? style.dropdownActive : ""
                      }`}
                      onClick={() => chooseCompanyOption(opt)}
                      onMouseEnter={() => setCompanyActiveIdx(idx)}
                    >
                      <span className={style.dropdownNewIcon}>+</span>
                      <span className={style.dropdownNewText}>
                        <Trans
                          i18nKey="signup.detail.registerNewCompany"
                          values={{ keyword: companyKeyword }}
                          components={{ strong: <strong /> }}
                        />
                      </span>
                    </button>
                  );
                }
                return (
                  <button
                    type="button"
                    key={opt.value}
                    ref={scrollRef}
                    className={`${style.dropdownItem} ${
                      active ? style.dropdownActive : ""
                    }`}
                    onClick={() => chooseCompanyOption(opt)}
                    onMouseEnter={() => setCompanyActiveIdx(idx)}
                  >
                    <img
                      className={style.dropdownItemIcon}
                      src={`${process.env.REACT_APP_CLIENT_URL}img/signup/company.svg`}
                      alt=""
                    />
                    <span className={style.dropdownItemText}>
                      {highlightMatch(opt.value, companyKeyword)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className={style.field}>
          <img
            className={style.fieldIcon}
            src={`${process.env.REACT_APP_CLIENT_URL}img/signup/user_id.svg`}
            alt=""
          />
          <input
            className={style.input}
            type="text"
            placeholder={t("signup.detail.namePlaceholder")}
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />
        </div>

        {!hidePassword && (
          <>
        <div className={style.field}>
          <img
            className={style.fieldIcon}
            src={`${process.env.REACT_APP_CLIENT_URL}img/signup/password.svg`}
            alt=""
          />
          <input
            className={style.input}
            type={showPassword ? "text" : "password"}
            placeholder={t("signup.detail.passwordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <button
            type="button"
            className={style.eyeBtn}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={
              showPassword
                ? t("signup.detail.hidePassword")
                : t("signup.detail.showPassword")
            }
          >
            <img
              src={showPassword ? eyeIconOpen : eyeIconHide}
              alt=""
            />
          </button>
        </div>

        <div className={style.field}>
          <img
            className={style.fieldIcon}
            src={`${process.env.REACT_APP_CLIENT_URL}img/signup/password.svg`}
            alt=""
          />
          <input
            className={`${style.input} ${
              confirmMismatch ? style.inputError : ""
            }`}
            type={showPasswordConfirm ? "text" : "password"}
            placeholder={t("signup.detail.passwordConfirmPlaceholder")}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            autoComplete="new-password"
          />
          <button
            type="button"
            className={style.eyeBtn}
            onClick={() => setShowPasswordConfirm((v) => !v)}
            aria-label={
              showPasswordConfirm
                ? t("signup.detail.hidePassword")
                : t("signup.detail.showPassword")
            }
          >
            <img
              src={showPasswordConfirm ? eyeIconOpen : eyeIconHide}
              alt=""
            />
          </button>
        </div>

        {confirmMismatch && (
          <div className={style.errorBlock}>
            <img
              className={style.errorIcon}
              src={`${process.env.REACT_APP_CLIENT_URL}img/common/warm-circle.svg`}
              alt=""
            />
            <span>{t("signup.detail.passwordMismatch")}</span>
          </div>
        )}

        <div
          className={style.rulesBox}
          style={{
            "--rule-check-icon": `url(${process.env.REACT_APP_CLIENT_URL}img/signup/warm_check.svg)`,
          }}
        >
          <PasswordRule active={ruleMinLen}>
            {t("signup.detail.ruleMinLen")}
          </PasswordRule>
          <PasswordRule active={ruleCombo}>
            {t("signup.detail.ruleCombo")}
            <br />
            <span className={style.ruleHint}>
              ! @ # $ % ^ * ( ) _ + - = [ ] {"{"} {"}"} | : ; " ' &lt; &gt; , . ? /
            </span>
          </PasswordRule>
          <PasswordRule active={ruleNoEdgeSpace}>
            {t("signup.detail.ruleNoEdgeSpace")}
          </PasswordRule>
        </div>
          </>
        )}

        <button
          type="button"
          className={`${style.submitBtn} ${
            hidePassword ? style.submitBtnNoPassword : ""
          } ${canSubmit ? style.submitBtnActive : style.submitBtnDisabled}`}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {t("signup.detail.submit")}
        </button>
      </div>
    </div>
  );
}

function highlightMatch(name, keyword) {
  const idx = name.toLowerCase().indexOf(keyword.toLowerCase());
  if (idx === -1) return name;
  return (
    <>
      {name.slice(0, idx)}
      <span className={style.match}>
        {name.slice(idx, idx + keyword.length)}
      </span>
      {name.slice(idx + keyword.length)}
    </>
  );
}

function PasswordRule({ active, children }) {
  return (
    <div className={style.rule}>
      <span
        className={`${style.ruleCheck} ${
          active ? style.ruleCheckActive : ""
        }`}
      />
      <span className={style.ruleText}>{children}</span>
    </div>
  );
}

export default SignUpDetail;
